import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authClient } from '../lib/auth-client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/user', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          let parsedPermissions = {};
          try {
            parsedPermissions = data.permissions ? JSON.parse(data.permissions) : {};
          } catch { /* ignore */ }
          const currentUser = {
            id: data.user_id,
            email: data.email,
            name: data.name,
            role: data.role,
            is_superuser: data.is_superuser,
            permissions: parsedPermissions,
            blog_id: data.blog_id,
            blog_subdomain: data.blog_subdomain
          };
          setUser(currentUser);
          setIsAuthenticated(true);
          return currentUser;
        }
      }
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } catch (e) {
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refreshSession().finally(() => setLoading(false));
  }, [refreshSession]);

  const isSuperuser = user?.is_superuser === true;
  const hasPermission = (key) => isSuperuser || !!user?.permissions?.[key];

  const openLoginModal = () => setLoginModalOpen(true);
  const closeLoginModal = () => setLoginModalOpen(false);

  const loginWithPassword = async (email, password) => {
    const { error } = await authClient.signIn.email({ email, password });
    if (error) return { error: error.message || 'Invalid email or password.' };
    const currentUser = await refreshSession();
    if (!currentUser) return { error: 'You do not have access to the admin area yet. Ask a superuser to grant you access.' };
    return { success: true, user: currentUser };
  };

  const signUp = async (email, password, name) => {
    const { error } = await authClient.signUp.email({ email, password, name });
    if (error) return { error: error.message || 'Could not create your account.' };
    const currentUser = await refreshSession();
    if (!currentUser) return { error: 'Account created. Ask a superuser to grant you admin access before you can sign in.' };
    return { success: true, user: currentUser };
  };

  const logout = async () => {
    await authClient.signOut();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isSuperuser,
      hasPermission,
      blogId: user?.blog_id,
      blogSubdomain: user?.blog_subdomain,
      permissions: user?.permissions,
      loading,
      loginModalOpen,
      openLoginModal,
      closeLoginModal,
      loginWithPassword,
      signUp,
      logout,
      refreshSession
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);